import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import iconTriangleDown from '../../../assets/icons/triangle-down.svg'

export type TreeViewNode = {
  id: string
  props: { [key: string]: unknown }
  nodes?: TreeViewNode[]
}

type TreeViewProps = {
  nodes: TreeViewNode[]
  indent?: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemComponent(props: any): JSX.Element
  expandAll?: boolean
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${({ indent = 0 }: Pick<TreeViewProps, 'indent'>) => ({
    marginLeft: indent ? '26px' : '0',
  })}
`
const ItemWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  margin-bottom: 12px;
`

const ExpandButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 10px;
  height: 10px;
  margin-right: 10px;
`

const ExpandButton = styled.img`
  ${({ expanded }: { expanded: boolean }) => ({
    transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
  })}
`

type ItemProps = {
  id: string
  expandable: boolean
  onExpand(id: string): void
}

const Item: React.FC<{ expanded: boolean } & ItemProps> = ({ id, expanded, expandable, onExpand, children }) => (
  <ItemWrapper onClick={() => onExpand(id)}>
    <ExpandButtonWrapper>
      {expandable && <ExpandButton src={iconTriangleDown} expanded={expanded} />}
    </ExpandButtonWrapper>
    {children}
  </ItemWrapper>
)

export const TreeView: React.FC<TreeViewProps> = ({ nodes, indent = 0, itemComponent, expandAll }) => {
  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const onExpand = useCallback(
    (id: string) => {
      if (expandedIds.includes(id)) {
        setExpandedIds(expandedIds.filter((eid: string) => eid !== id))
      } else {
        setExpandedIds([...expandedIds, id])
      }
    },
    [setExpandedIds, expandedIds],
  )

  return (
    <Wrapper indent={indent}>
      {nodes.map((node: TreeViewNode) => {
        const childNodes = node.nodes
        const isExpanded = expandedIds.includes(node.id) || !!expandAll

        return (
          <div key={node.id}>
            <Item id={node.id} expanded={isExpanded} expandable={!!node.nodes?.length} onExpand={onExpand}>
              {itemComponent(node.props)}
            </Item>
            {childNodes && !!childNodes.length && isExpanded && (
              <TreeView indent={indent + 1} nodes={childNodes} itemComponent={itemComponent} />
            )}
          </div>
        )
      })}
    </Wrapper>
  )
}
